// Tenant currency formatter. Reads currency from /api/t/settings/company once and caches.
// Falls back to NGN/₦. Used app-wide as `useCurrency()` → { fmt, symbol, code }.
import { createContext, useContext, useEffect, useState } from "react";
import { api } from "./auth";

const SYMBOLS = {
  NGN: "₦", USD: "$", EUR: "€", GBP: "£", KES: "KSh", ZAR: "R", GHS: "₵",
  INR: "₹", AUD: "A$", CAD: "C$",
};

// ---------- Module-level helpers (no hook required) ----------
function readTenantCurrency() {
  try {
    const t = JSON.parse(localStorage.getItem("brivo_tenant") || "{}");
    return (t.currency || "NGN").toUpperCase();
  } catch { return "NGN"; }
}

export function getCurrencySymbol() {
  return SYMBOLS[readTenantCurrency()] || (readTenantCurrency() + " ");
}

export function fmtMoney(n) {
  return `${getCurrencySymbol()}${Number(n || 0).toLocaleString(undefined, { minimumFractionDigits: 2, maximumFractionDigits: 2 })}`;
}

const CurrencyCtx = createContext({ code: "NGN", symbol: "₦", fmt: fmtMoney });

export function CurrencyProvider({ children }) {
  const [code, setCode] = useState(readTenantCurrency());
  useEffect(() => {
    if (!localStorage.getItem("brivo_token") || !localStorage.getItem("brivo_tenant")) return;
    api.get("/t/settings/company")
      .then((r) => {
        const c = (r.data?.currency || "NGN").toUpperCase();
        setCode(c);
        // Persist to tenant blob so module-level fmtMoney also reflects current setting
        try {
          const t = JSON.parse(localStorage.getItem("brivo_tenant") || "{}");
          if (t.currency !== c) {
            localStorage.setItem("brivo_tenant", JSON.stringify({ ...t, currency: c }));
          }
        } catch {}
      })
      .catch(() => {});
  }, []);
  const symbol = SYMBOLS[code] || code + " ";
  const fmt = (n) => `${symbol}${Number(n || 0).toLocaleString(undefined, { minimumFractionDigits: 2, maximumFractionDigits: 2 })}`;
  return <CurrencyCtx.Provider value={{ code, symbol, fmt }}>{children}</CurrencyCtx.Provider>;
}

export function useCurrency() {
  return useContext(CurrencyCtx);
}
